import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import { AppUtils } from "source/framework/appUtils";
import { List } from "../../framework/List/List";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { ListSearchContainer } from "source/components/framework/List/ListSearchContainer";
import { IReadReceiptListItem } from "ui/Scripts/source/models/readReceipt/readReceiptListItem";
import { ReadReceiptListItems } from "ui/Scripts/source/models/readReceipt/readReceiptListItems";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Box } from "@mui/material";

export interface IReadReceiptListProp {
    companyId: string;
    recordId: string;
    height?: string;
    getReadReceiptListItems: (companyId: string, recordId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean) => Promise<ReadReceiptListItems>;
}

interface IReadReceiptListState {
    // List
    intialized: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: IReadReceiptListItem[];
    selectedId: string;
}

export class ReadReceiptList extends React.Component<IReadReceiptListProp, IReadReceiptListState> {
    constructor(props: IReadReceiptListProp) {
        super(props);
        this.state = {
            intialized: false,
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: "",
            pageSize: infiniteListPageSize,
            sortColumn: "ModifyDate",
            sortOrderIsAsc: false,
            items: [],
            selectedId: null,
        };
    }

    searchItems = async (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean) => {
        const props = this.props;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => props.getReadReceiptListItems(props.companyId, props.recordId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc), false, true, () => this.setState({ isLoading: false }));
        if (!result) return;
        const state = this.state;
        const items = List.getListItems(state.items, state.selectedId, [], result.items, resetItems, refreshList);
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId: items.selectedId
        });
    };

    refreshList = async () => {
        const state = this.state;
        await this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, true);
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentDidUpdate(prevProps: IReadReceiptListProp, prevState: IReadReceiptListState) {
        const props = this.props;
        const state = this.state;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevRecordId = prevProps.recordId ? prevProps.recordId : "";
        const recordId = props.recordId ? props.recordId : "";
        if (companyId === prevCompanyId && recordId === prevRecordId) return;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <ListSearchContainer sx={{ margin: "0 0 1rem 0" }}>
                    <ListSearchFilter
                        searchFilter={state.filter}
                        onSearchClick={this.changeFilter}
                    />
                </ListSearchContainer>
                <List
                    intialized={state.intialized}
                    isLoading={state.isLoading}
                    hasMore={state.hasMore}
                    multiSelect={false}
                    globalLoadingInUse={false}
                    height={props.height}
                    header={{
                        columns: [
                            { size: 3, column: "ModifyDate", content: Translations.Time },
                            { size: 5, column: "UserName", content: Translations.User },
                            { size: 4, column: "UserCompanyName", content: Translations.Company }
                        ]
                    }}
                    lines={state.items.map((item) => {
                        return {
                            columns: [
                                { size: 3, content: Base.timeToDateTimeStr(item.modifyUser.date) },
                                {
                                    size: 5,
                                    content: <Box display="flex" sx={{ overflow: "hidden" }}>
                                        <AvatarImage
                                            id={item.modifyUser.code}
                                            name={item.modifyUser.name}
                                            image={item.modifyUser.picture}
                                            tooltip={item.modifyUser.name}
                                        />
                                        <Box display="flex" sx={{ marginLeft: ".5rem", overflow: "hidden" }}>
                                            {item.modifyUser.name}
                                        </Box>
                                    </Box>
                                },
                                {
                                    size: 4,
                                    content: <Box display="flex" sx={{ overflow: "hidden" }}>
                                        <AvatarImage
                                            id={item.userCompanyName}
                                            name={item.userCompanyName}
                                            image={item.userCompanyLogo}
                                            tooltip={item.userCompanyName}
                                        />
                                        <Box display="flex" sx={{ marginLeft: ".5rem", overflow: "hidden" }}>
                                            {item.userCompanyName}
                                        </Box>
                                    </Box>
                                }
                            ],
                            id: item.id
                        };
                    })}
                    sortColumn={state.sortColumn}
                    sortOrderIsAsc={state.sortOrderIsAsc}
                    selectedId={state.selectedId}
                    checkedIds={[]}
                    onSetSelectedIds={this.handleSetSelectedIds}
                    onSearchLines={this.handleSearchLines}
                    onOpenLine={null}
                />
            </>
        );
    }
}